import genericApi from './genericApi';

import Awb from '../models/Awb';
import ApiResponse from '../models/ApiResponse';
import PickupOrder from '../models/PickupOrder';
import PickupOrderFwd from '../models/PickupOrderFwd';
import PickupOrderGha from '../models/PickupOrderGha';
import OrderWhs from '../models/OrderWhs';
import GhaAwbLocationPcs from '../models/GhaAwbLocationPcs';
import GhaPoAwbsPayments from '../models/GhaPoAwbsPayments';
import GhaWhPoAwbException from '../models/GhaWhPoAwbException';
import OrderDashboard from '../models/OrderDashboard';
import OrderWhsDetails from '../models/OrderWhsDetails';
import GhaPickupStatusAwb from '../models/GhaPickupStatusAwb';
import GhaPickupStatus from '../models/GhaPickupStatus';
import OtherChecks from '../models/OtherChecks';
import { Value } from '../models/OtherChecksForm';

import { baseUrl } from './baseUrl';
import ExceptionTypes from '../models/ExceptionTypes';
import { InterlineChecks } from '../models/InterlineChecks';
import { SpecialOverallForm, SpecialsForm } from '../models/SpecialsData';

const poRequestHandler = {

  getPickupOrdersGha: (type: string | null, startDate: string, endDate: string, startTime: string, endTime: string, keyword: string, sort?: boolean, completed?: boolean, suspended?: boolean, dropOff?: boolean, pickup?: boolean): Promise<ApiResponse<Array<OrderDashboard>>> => {

    return commonPickupOrderParams('office', type, startDate, endDate, startTime, endTime, keyword, sort, completed, suspended, dropOff, pickup);
  },
  getPickupOrdersFwd: (type: string, startDate: string, endDate: string, startTime: string, endTime: string, keyword: string, sort?: boolean, completed?: boolean, suspended?: boolean): Promise<ApiResponse<Array<PickupOrderFwd>>> => {

    const usedFor = type === 'go' ? 'gha' : 'forwarder';
    return commonPickupOrderParams(usedFor, type, startDate, endDate, startTime, endTime, keyword, sort, completed, suspended);
  },
  getPickupOrdersWhs: (type: string, date: string): Promise<ApiResponse<Array<OrderWhs>>> => {

    return commonPickupOrderParams('warehouse', type, date, '', '', '', '', true);
  },
  getOrdersWhsSearch: (keyword: string): Promise<ApiResponse<Array<PickupOrderFwd>>> => {

    return commonPickupOrderParams('warehouse', null, '', '', '', '', keyword, true)
  },
  getLastPickupOrder: (type: string): Promise<ApiResponse<PickupOrder>> => {

    const exp: string = type === 'export' ? '?isExport=true' : '';
    return genericApi.get(`latest_pickup_order${exp}`);
  },
  updatePickupOrder: (pickupOrder: PickupOrder, direct?: boolean): Promise<ApiResponse<PickupOrder>> => {

    let url = `pickup_order/${pickupOrder.number}`;
    if (direct) {
      url += `?direct=${direct}`;
    }
    return genericApi.post(url, pickupOrder);
  },
  notifyTruckingCompany: (poNumber: string, contact: string, doNotify: boolean): Promise<ApiResponse<PickupOrder>> => {

    return genericApi.post(`pickup_order/${poNumber}/dispatcher/${contact}?notif=${doNotify}`);
  },
  bulkConvertToGoToPo: (awbs: Array<{ code: string, number: string, go_number: string }>, id_forwarder?: number, paymentType?: string, paymentInfo?: string, stripePaymentMethodId?: string): Promise<ApiResponse<{ number: string }>> => {
    return genericApi.post(`bulk_convert_to_po`, { awbs, id_forwarder, payment_type: paymentType, payment_info: paymentInfo, stripe_payment_method_id: stripePaymentMethodId });
  },
  driverCheckIn: (poNumber: string, secretKey: string, location?: { lat: number, lng: number }): Promise<ApiResponse<boolean>> => {

    let url = `pickup_order/${poNumber}/checkin?key=${secretKey}`;
    if (location) {
      let locationString: string = `${location.lat},${location.lng}`;
      url += `&location=${locationString}`;
    }
    return genericApi.put(url);
  },
  getAnonymousPickupOrder: (pickupOrderId: string, key: string): Promise<ApiResponse<PickupOrder>> => {

    return genericApi.get(`pickup_order/${pickupOrderId}?key=${key}`);
  },
  saveDriverInfo: (pickupOrderId: string, key: string, data: PickupOrder): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${pickupOrderId}/driver_info?key=${key}`, data);
  },
  getPickupOrder: (pickupOrderId: string, key?: string): Promise<ApiResponse<PickupOrder>> => {

    const keyParam: string = key ? `?key=${key}` : '';

    return genericApi.get(`pickup_order/${pickupOrderId}${keyParam}`);
  },
  getPickupOrderWhs: (pickupOrderId: string, key?: string, start?: boolean): Promise<ApiResponse<OrderWhsDetails>> => {

    const keyParam: string = key ? `&key=${key}` : '';

    return genericApi.get(`pickup_order/${pickupOrderId}?usedFor=warehouse${keyParam}&start=${start}`);
  },
  notifyDriver: (key: string, pickupOrder: PickupOrder, direct?: boolean): Promise<ApiResponse<boolean>> => {

    let url = `pickup_order/${pickupOrder.number}/driver_info?key=${key}&notify=${true}`;
    if (direct) {
      url += `&direct=${direct}`;
    }
    return genericApi.put(url, pickupOrder);
  },

  notifyDriverDirect: (pickupOrder: PickupOrder, direct: boolean): Promise<ApiResponse<boolean>> => {

    let url = `pickup_order/${pickupOrder.number}/driver_info?notify=${true}&direct=${direct}`;
    return genericApi.put(url, pickupOrder);
  },
  setETA: (poNumber: string, key: string, time: string): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/eta?key=${key}&eta=${time}`);
  },
  getGhaPickupOrderList: (pickupOrderId: string): Promise<ApiResponse<PickupOrderGha>> => {

    return genericApi.get(`pickup_order/${pickupOrderId}`);
  },
  getGhaPoAwbsPayments: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<GhaPoAwbsPayments>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/payment`);
  },
  getGhaPoAwbWhLocationAndPcs: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<GhaPickupStatusAwb>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/wh_location`);
  },
  getGhaPoAwb: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<GhaPickupStatus>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}`);
  },
  addGhaPoAwbWhLocationAndPcs: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: GhaAwbLocationPcs): Promise<ApiResponse<GhaPickupStatusAwb>> => {

    return genericApi.put(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/wh_location`, data);
  },
  deleteGhaPoAwbWhLocationAndPcs: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: GhaAwbLocationPcs): Promise<ApiResponse<GhaPickupStatusAwb>> => {

    return genericApi.delete(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/wh_location`, data);
  },
  updateGhaPoAwbWhCustomsClearanceStatus: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: { customs_clearance: boolean; }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/customs_clearance`, data);
  },
  updateGhaPoAwbSecurityChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: GhaPickupStatusAwb): Promise<ApiResponse<GhaPickupStatus>> => {

    return genericApi.post(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/security_checks`, data);
  },
  updateGhaWhPoAwbDriverId: (poNumber: string, driverId: string, driverIdType: string): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/driver_id`, { driver_id: driverId, driver_id_type: driverIdType });
  },
  doGhaWhPoLock: (poNumber: string, locked: boolean): Promise<ApiResponse<{ lock_status: string; gha_wh_user_name: string; }>> => {

    return genericApi.post(`pickup_order/${poNumber}/lock`, { locked });
  },
  setGhaWhPoAwbDock: (poNumber: string, dockID: number | null): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/dock`, { dock_id: dockID });
  },
  setSealLockVerify: (poNumber: string, sealLockVerified: boolean | null): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/seal_lock_verify`, { seal_lock_verified: sealLockVerified });
  },
  setGhaWhPoAwbTruckOnDock: (poNumber: string): Promise<ApiResponse<boolean>> => {

    return genericApi.post(`pickup_order/${poNumber}/truck_on_dock`);
  },
  getGhaPoAwbWhExceptions: (poNumber: string, awbCode: string, awbNumber: string): Promise<ApiResponse<GhaWhPoAwbException>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/exception`);
  },
  updateGhaPoAwbWhExceptions: (poNumber: string, awbCode: string, awbNumber: string, data: GhaWhPoAwbException): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/exception`, data);
  },
  constructAttachmentUrl: (key: string) => {

    return `${baseUrl}pickup_order/exception/attachment/${key}`;
  },
  uploadGhaPoAwbWhExceptionsAttachments: (poNumber: string, awbCode: string, awbNumber: string, data: FormData): Promise<ApiResponse<string[]>> => {

    return genericApi.postFile(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/exception/attachment`, data);
  },
  uploadGhaPoWhExceptionsAttachments: (poNumber: string, data: FormData): Promise<ApiResponse<string[]>> => {

    return genericApi.postFile(`pickup_order/${poNumber}/exception/attachment`, data);
  },
  deleteGhaPoAwbWhExceptionsAttachment: (attachmentKey: string): Promise<ApiResponse<string[]>> => {

    return genericApi.delete(`pickup_order/awb/exception/attachment/${attachmentKey}`);
  },
  deleteGhaPoWhExceptionsAttachment: (attachmentKey: string): Promise<ApiResponse<string[]>> => {

    return genericApi.delete(`pickup_order/exception/attachment/${attachmentKey}`);
  },
  getGhaPoNote: (poNumber: string): Promise<ApiResponse<{ note: string, attachments: string[] }>> => {

    return genericApi.get(`pickup_order/${poNumber}/note`);
  },
  updateGhaPoNote: (poNumber: string, data: { note: string }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/note`, data);
  },
  getGoodsDeclaration: (poNumber: string): Promise<ApiResponse<{ dangerous_goods: boolean }>> => {

    return genericApi.get(`pickup_order/${poNumber}/dangerous_goods`);
  },
  updateGoodsDeclaration: (poNumber: string, data: { value: boolean }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/dangerous_goods`, data);
  },
  setSecurityDetails: (orderNumber: string, data: { sealed: boolean; seal_number: string; locked: boolean; iac_number?: string }, key?: string): Promise<ApiResponse<{ sealed: boolean; seal_number: string; locked: boolean; iac_number?: string; valid_transportation?: boolean; }>> => {

    const keyParam: string = key ? `?key=${key}` : '';

    return genericApi.post(`pickup_order/${orderNumber}/security_details${keyParam}`, data);
  },
  saveGhaExportDriverInfo: (pickupOrderId: string, data: any): Promise<ApiResponse<PickupOrderGha>> => {

    return genericApi.put(`pickup_order/${pickupOrderId}/driver_info`, data);
  },
  getGhaPoAwbOtherChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<OtherChecks>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/r4cform`);
  },
  getGhaPoAwbSpecialChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<SpecialOverallForm>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/specials_form`);
  },
  getGhaPoAwbInterlineChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number): Promise<ApiResponse<InterlineChecks>> => {

    return genericApi.get(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/interline_form`);
  },
  getCarrierList: (): Promise<ApiResponse<any>> => {

    return genericApi.get(`carriers`);
  },
  updateGhaPoInterlineChecks: (poNumber: string, val?: string): Promise<ApiResponse<InterlineChecks>> => {

    return genericApi.post(`pickup_order/${poNumber}/interline_form`, { carrier: val });
  },
  updateGhaPoAwbInterlineChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: { 'interline_form_ver': number, 'interline_form_data': Array<Value> }, val?: string): Promise<ApiResponse<InterlineChecks>> => {

    return genericApi.post(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/interline_form?val=${val}`, data);
  },
  updateGhaPoAwbOtherChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: { 'r4c_form_ver': number, 'r4c_form_data': Array<Value>, 'r4c_form_ver_flight'?: number }): Promise<ApiResponse<OtherChecks>> => {

    return genericApi.post(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/r4cform`, data);
  },
  updateGhaPoAwbSpecialsChecks: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: { 'specials_form_ver': number, 'specials_form_data': Array<Value> }): Promise<ApiResponse<SpecialOverallForm>> => {

    return genericApi.post(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/specials_form`, data);
  },
  driverSetDropOfDate: (poNumber: string, secretKey: string, data: { pickup_date: string }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/date_time?key=${secretKey}`, data);
  },
  saveCWTData: (pickupOrderId: string, awbCode: string, awbNumber: string, data: any, actualScaleWeight: string): Promise<ApiResponse<boolean>> => {
    return genericApi.put(`pickup_order/${pickupOrderId}/awb/${awbCode}/${awbNumber}/cwt_data`, { cwt_data: data, actual_scale_weight: actualScaleWeight });
  },

  savePodData: (pickupOrderId: string, awbCode: string, awbNumber: string, data: ExceptionTypes): Promise<ApiResponse<boolean>> => {
    return genericApi.put(`pickup_order/${pickupOrderId}/awb/${awbCode}/${awbNumber}/pod_data`, { pod_data: data });
  },
  calculateCWT: (data: any): Promise<ApiResponse<{ cwt: number }>> => {

    return genericApi.post('cwt', { cwt_data: data });
  },
  savePoAwbInfoToWh: (poNumber: string, awbCode: string, awbNumber: string, ver: number, data: { 'info_to_wh': string }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/${ver}/info_to_wh`, data);
  },
  savePoAwbRemarks: (poNumber: string, awbCode: string, awbNumber: string, data: { 'general_remarks': string, 'customer_error': boolean }): Promise<ApiResponse<boolean>> => {

    return genericApi.put(`pickup_order/${poNumber}/awb/${awbCode}/${awbNumber}/general_remarks`, data);
  },
  getGhaOrderAwbs: (poNumber: string): Promise<ApiResponse<Array<Awb>>> => {

    return genericApi.get(`pickup_order/${poNumber}/awbs`);
  },

  removeOrderConn: (type: string): Promise<ApiResponse<boolean>> => {
    return genericApi.delete('orders?type=' + type);
  },

  suspendOrder: (orderNumber: string, data: any): Promise<ApiResponse<boolean>> => {
    return genericApi.put(`suspend_order/${orderNumber}`, data);
  },
  
  cancelOrder: (orderNumber: string): Promise<ApiResponse<boolean>> => {
    return genericApi.post(`cancel_order/${orderNumber}`);
  }, 
};

export default poRequestHandler;

function commonPickupOrderParams(usedFor: string, type: string | null, startDate: string, endDate: string, startTime: string, endTime: string, keyword: string, sort?: boolean, completed?: boolean, suspended?: boolean, dropoff?: boolean, pickup?: boolean) {

  let url: string = `pickup_orders?usedFor=${usedFor}&`;

  if (type) {
    url = `${url}type=${type}&`;
  };

  if (startDate) {
    url = `${url}start_date=${startDate}&`;
  };

  if (endDate) {
    url = `${url}end_date=${endDate}&`;
  };

  if (startTime) {
    url = `${url}start_time=${startTime}&`;
  };

  if (endTime) {
    url = `${url}end_time=${endTime}&`;
  };

  if (keyword) {
    url = `${url}keyword=${keyword}&`;
  };
  if (completed !== undefined) {
    url = `${url}completed=${completed}&`;
  };
  if (suspended !== undefined) {
    url = `${url}suspended=${suspended}&`;
  };
  if (dropoff !== undefined) {
    url = `${url}dropoff=${dropoff}&`;
  };
  if (pickup !== undefined) {
    url = `${url}pickup=${pickup}&`;
  };

  if (sort) {
    url = `${url}sort=true`;
  };

  return genericApi.get(url);
}